import React from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import './transports-modal.css';

/**
 * Modal component to display transport details for an offer line
 * Shows a table with transport information including driver, vehicle, and delivery details
 */
const TransportsModal = ({ isOpen, onClose, transports = [] }) => {
  const { t } = useTranslation();

  const parseTransportDate = (dateString) => {
    // Handle ISO format (2026-04-01T06:30:00)
    if (dateString.includes('T')) {
      return new Date(dateString);
    }
    
    // Convert DD/MM/YYYY HH:MM:SS to Date object
    const parts = dateString.split(' ');
    const dateParts = parts[0].split('/');
    const timePart = parts[1] || '00:00:00';
    
    // dateParts[0] = day, dateParts[1] = month, dateParts[2] = year
    const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T${timePart}`;
    return new Date(formattedDate);
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = parseTransportDate(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      // Format as DD/MM/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = parseTransportDate(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }
      // Format as HH:MM
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (e) {
      return '-';
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="transports-modal">
      <ModalHeader toggle={onClose}>
        <i className="mdi mdi-truck"></i>&nbsp;
        {t('offers.transports')}
      </ModalHeader>
      <ModalBody>
        {transports && transports.length > 0 ? (
          <Table hover responsive>
            <thead>
              <tr>
                <th>{t('offers.transport.number') || '#'}</th>
                <th>{t('offers.transport.departure') || 'Departure'}</th>
                <th>{t('offers.transport.driver') || 'Driver'}</th>
                <th>{t('offers.transport.pigs') || 'Pigs'}</th>
              </tr>
            </thead>
            <tbody>
              {transports.map((transport, index) => (
                <tr key={index}>
                  <td className="text-center">
                    <strong>{transport.devranr}</strong>
                  </td>
                  <td className="departure-cell">
                    <div className="departure-date">{formatDateOnly(transport.deabddt)}</div>
                    <div className="departure-time">{formatTimeOnly(transport.deabddt)}</div>
                  </td>
                  <td>
                    <div className="transport-driver-info">
                      <strong>{transport.dechfnm || '-'}</strong>
                      <small className="d-block text-muted">
                        {transport.debednm || '-'}
                      </small>
                      <a href={`tel:${transport.degsmnr}`} className="text-decoration-none d-block mt-1">
                        <i className="mdi mdi-phone"></i> {transport.degsmnr || '-'}
                      </a>
                    </div>
                  </td>
                  <td className="text-center">
                    <strong>{transport.deaanta || '-'}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="alert alert-info" role="alert">
            {t('offers.noTransports') || 'No transports available'}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {t('general.close') || 'Close'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TransportsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transports: PropTypes.arrayOf(
    PropTypes.shape({
      deactie: PropTypes.string,
      devranr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      deabddt: PropTypes.string,
      deaanta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      delvanr: PropTypes.string,
      debednm: PropTypes.string,
      dechfce: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dechfnm: PropTypes.string,
      degsmnr: PropTypes.string,
    })
  ),
};

export default TransportsModal;
